import { supabase } from "@/integrations/supabase/client";

// Single source of truth for the native PKCE code exchange.
//
// On native there are TWO possible delivery paths for the OAuth code:
//   1. The com.aivyv.mirror://auth/callback custom-scheme deep link, caught by
//      App.addListener('appUrlOpen') in App.tsx.
//   2. An INTERNAL webview navigation to capacitor://localhost/auth?code=...
//      (observed on iOS 18.7 / Capacitor 8), where appUrlOpen does NOT fire and
//      the /auth route mounts with the code in window.location.search.
//
// The PKCE flow state is single-use: exchanging the same code twice fails with
// "invalid flow state, no valid flow state found" (Sentry VYV-FRONTEND-S). This
// module dedupes by code value so the two paths can never both exchange — only
// the first wins; the second is a no-op skip.
const handledCodes = new Set<string>();
let inFlight = false;

// Pull the `code` query param out of a full URL or custom-scheme deep link.
// URL can't reliably parse custom schemes for search params across engines, so
// split on the first '?' and use URLSearchParams on the query string.
function extractCode(urlOrCode: string): string | null {
  if (!urlOrCode) return null;
  const queryIndex = urlOrCode.indexOf("?");
  if (queryIndex === -1) {
    // No query string — treat the whole thing as a bare code only if it has no
    // scheme separator (otherwise it's a URL with no code).
    return urlOrCode.includes("://") ? null : urlOrCode;
  }
  try {
    const params = new URLSearchParams(urlOrCode.slice(queryIndex + 1));
    return params.get("code");
  } catch {
    return null;
  }
}

// Strip ?code=&state= from the webview URL so a re-render / re-mount can't
// reprocess it. replaceState keeps the capacitor://localhost origin + path.
export function cleanCodeFromUrl(): void {
  try {
    if (typeof window === "undefined") return;
    if (!window.location.search.includes("code=")) return;
    window.history.replaceState({}, "", window.location.pathname);
  } catch {
    /* replaceState unavailable — non-fatal */
  }
}

export type NativeExchangeStatus = "ok" | "error" | "skipped" | "no_code";

export interface NativeExchangeResult {
  status: NativeExchangeStatus;
  error?: unknown;
}

// Exchange the PKCE code for a session AT MOST ONCE per code. Cleans the code
// from the webview URL on success. Returns 'skipped' when the code was already
// handled or another exchange is in flight.
export async function exchangeNativeOAuthOnce(
  urlOrCode: string,
): Promise<NativeExchangeResult> {
  const code = extractCode(urlOrCode);
  if (!code) return { status: "no_code" };
  if (inFlight || handledCodes.has(code)) return { status: "skipped" };

  inFlight = true;
  try {
    const { error } = await supabase.auth.exchangeCodeForSession(urlOrCode);
    if (error) return { status: "error", error };
    handledCodes.add(code);
    cleanCodeFromUrl();
    return { status: "ok" };
  } catch (error) {
    return { status: "error", error };
  } finally {
    inFlight = false;
  }
}
